import RGBColor from 'classes/rgb-color';
import HEXColor from 'classes/hex-color';
import {
  DocumentReviewCategory,
  ReviewCategory,
  StatisticByStatus,
} from './review-category';

export enum FailureReason {
  missingAnnotation = 'missingAnnotation',
  notPresentedAnnotation = 'notPresentedAnnotation',
}

export enum ReviewType {
  font = 'font',
  safety = 'safety',
  branding = 'branding',
  claims = 'claims',
  claimsOrder = 'claimsOrder',
}

export enum ReviewCharacteristic {
  fontType = 'fontType',
  fontColor = 'fontColor',
  fontSize = 'fontSize',
  ssiCheck = 'ssiCheck',
  ssiAnnotation = 'ssiAnnotation',
  ssiPresence = 'ssiPresence',
  productName = 'productName',
  claimText = 'claimText',
  claimStatus = 'claimStatus',
  claimsDependentContent = 'claimsDependentContent',
  claimsOrder = 'claimsOrder',
}

export enum ReviewResult {
  fail = 'fail',
  pass = 'pass',
  skip = 'skip',
  warning = 'warning',
}

export const ReviewResultList: ReviewResult[] = [
  ReviewResult.fail,
  ReviewResult.pass,
  ReviewResult.warning,
];

export enum SplitMode {
  split = 'split',
  undivided = 'undivided',
}

export enum ColorSuggestionValueKeys {
  hex = 'hex',
  rgb = 'rgb',
}

export enum ValueKeys {
  fontFamily = 'fontFamily',
  size = 'size',
  ssiCheck = 'ssiCheck',
  ssiAnnotation = 'ssiAnnotation',
  brandingType = 'brandingType',
  ssiPresence = 'ssiPresence',
  claimText = 'claimText',
  claimStatus = 'claimStatus',
}

export enum PageGenericErrorCode {
  NO_TEXT_DATA_FOUND = 'NO_TEXT_DATA_FOUND',
  PAGE_IS_IMAGE = 'PAGE_IS_IMAGE',
}

export type PageGenericError = {
  code: PageGenericErrorCode;
  message: string;
};

export enum ReviewCategoryErrorCode {
  NO_SSI_ANNOTATIONS = 'NO_SSI_ANNOTATIONS',
  PAGE_IS_IMAGE_STYLE = 'PAGE_IS_IMAGE_STYLE',
  PAGE_IS_IMAGE_SAFETY = 'PAGE_IS_IMAGE_SAFETY',
  PAGE_IS_IMAGE_BRANDING = 'PAGE_IS_IMAGE_BRANDING',
  PAGE_IS_IMAGE_CLAIMS = 'PAGE_IS_IMAGE_CLAIMS',
  PAGE_IS_IMAGE_CLAIMS_ERROR = 'PAGE_IS_IMAGE_CLAIMS_ERROR',
  CLAIMS_UNSUPPORTED_DOCUMENT_PRODUCT = 'CLAIMS_UNSUPPORTED_DOCUMENT_PRODUCT',
  CLAIMS_UNSUPPORTED_DOCUMENT_BRAND = 'CLAIMS_UNSUPPORTED_DOCUMENT_BRAND',
}

export type ReviewCategoryError = {
  code: ReviewCategoryErrorCode;
  message: string;
};

export const styleBlockers: ReviewCategoryErrorCode[] = [
  ReviewCategoryErrorCode.PAGE_IS_IMAGE_STYLE,
];

export const claimsBlockers: ReviewCategoryErrorCode[] = [
  ReviewCategoryErrorCode.PAGE_IS_IMAGE_CLAIMS,
  ReviewCategoryErrorCode.CLAIMS_UNSUPPORTED_DOCUMENT_PRODUCT,
  ReviewCategoryErrorCode.CLAIMS_UNSUPPORTED_DOCUMENT_BRAND,
];

export const coloredErrors: ReviewCategoryErrorCode[] = [
  ReviewCategoryErrorCode.PAGE_IS_IMAGE_SAFETY,
  ReviewCategoryErrorCode.PAGE_IS_IMAGE_BRANDING,
  ReviewCategoryErrorCode.PAGE_IS_IMAGE_CLAIMS_ERROR,
];

export const SuggestionValueKeys = {
  ...ColorSuggestionValueKeys,
  ...ValueKeys,
};

export type SuggestionValue = {
  [SuggestionValueKeys.fontFamily]?: string;
  [SuggestionValueKeys.hex]?: HEXColor;
  [SuggestionValueKeys.rgb]?: RGBColor;
  [SuggestionValueKeys.size]?: number;
  [SuggestionValueKeys.ssiCheck]?: string;
  [SuggestionValueKeys.ssiAnnotation]?: string;
  [SuggestionValueKeys.brandingType]?: string;
  [SuggestionValueKeys.ssiPresence]?: string;
  [SuggestionValueKeys.claimText]?: string;
  [SuggestionValueKeys.claimStatus]?: string;
};

export type CheckMessage = {
  code: string;
  message: string;
  type: 'ERROR' | 'WARNING';
};

export type ClaimNumber = {
  number: string;
  type: 'CLAIM_NUMBER' | 'ORDER_NUMBER';
  groupNumber?: string;
};

export type CheckMissingClaims = {
  claimNumber: ClaimNumber;
  claimType: string;
  claimText: string;
};

export type Check = {
  reviewType: ReviewType;
  characteristic: ReviewCharacteristic;
  result: ReviewResult;
  failureReason?: FailureReason;
  messages?: (string | CheckMessage)[];
  usedValues?: SuggestionValue[];
  requiredValues?: SuggestionValue[];
  claimsOrderUsedValues?: ClaimNumber[];
  claimsOrderRequiredValues?: ClaimNumber[];
  missingClaims?: CheckMissingClaims[];
  isExceptionGranted?: boolean;
};

export type CheckPayload = {
  reviewType: ReviewType;
  characteristic: ReviewCharacteristic;
  result: ReviewResult;
  failureReason?: FailureReason;
  messages?: (string | CheckMessage)[];
  usedValues?: ClaimNumber[] | SuggestionValue[];
  requiredValues?: ClaimNumber[] | SuggestionValue[];
  missingClaims?: CheckMissingClaims[];
};

export enum SsiWarningSafety {
  LINKED_SSI_DOCUMENT_IS_UNSUPPORTED = 'LINKED_SSI_DOCUMENT_IS_UNSUPPORTED',
  LINKED_SSI_DOCUMENT_IS_MISSED = 'LINKED_SSI_DOCUMENT_IS_MISSED',
  SSI_CHECK_FOR_UNBRANDED_IS_UNSUPPORTED = 'SSI_CHECK_FOR_UNBRANDED' +
    '_IS_UNSUPPORTED',
}

export enum SsiWarningStyle {
  STYLE_REVIEW_FOR_RESOURCE_TYPE_IS_UNSUPPORTED = 'STYLE_REVIEW_FOR_RESOURCE_' +
    'TYPE_IS_UNSUPPORTED',
}

export type SSIDetails = {
  ssiDocument?: SsiDocument;
  warnings?: {
    warningSafety: SsiWarningSafety | null;
    warningStyle: SsiWarningStyle | null;
  };
};

export type SsiDocument = {
  name: string;
  documentNumber: string;
};

export type DocumentReviewsPayload = {
  [DocumentReviewCategory.brandingReview]: {
    checks: CheckPayload[];
    result: ReviewResult;
  };
};

export type DocumentAlertsPayload = {
  [ReviewCategory.styleReview]: StatisticByStatus;
  [ReviewCategory.safetyReview]: StatisticByStatus;
  [ReviewCategory.claimsReview]: StatisticByStatus;
};

export type CMYK = {
  cyan: number;
  magenta: number;
  yellow: number;
  key: number;
};

export type RGB = {
  red: number;
  green: number;
  blue: number;
};
